import React, { ReactElement } from 'react';
import { Chart } from '@dexma/ui-components';
import Highcharts, { Options } from 'highcharts';
import HighchartsBullet from 'highcharts/modules/bullet';
import HighchartsHeatmap from 'highcharts/modules/heatmap';
import styled from 'styled-components';

import RichTextData from 'domain/entity/widgets/RichTextData';
import TextBlockWidget from 'adapter/in/common/components/widgets/TextBlock';
import ErrorWidget from 'adapter/in/common/components/widgets/Error';

HighchartsBullet(Highcharts);
HighchartsHeatmap(Highcharts);

type BusinessInsightProps = {
    height: number;
    width: number;
    isOnEdit: boolean;
    data: RichTextData;
    decimalSeparator: string;
    thousandSeparator: string;
    textBlockId: string;
    dashboardId?: string;
    isLoading: boolean;
};

const CHART_TEXT_GAP = 8;
const DATA_FEEDBACK_WARNING_TYPE = 'WARNING';

const BusinessInsightWrapper = styled.div`
    display: flex;
    flex-direction: column;
    height: 100%;
    min-height: 0;
    overflow: hidden;
`;

const ChartWrapper = styled.div<{ $height: number; $hasHtml: boolean }>`
    flex: 0 0 ${({ $height }) => `${$height}px`};
    height: ${({ $height }) => `${$height}px`};
    min-height: 0;
    margin-bottom: ${({ $hasHtml }) => ($hasHtml ? `${CHART_TEXT_GAP}px` : '0')};
`;

const HtmlWrapper = styled.div`
    flex: 1 1 auto;
    min-height: 0;
`;

const getChartOptions = (highcharts: RichTextData['highcharts'], width: number, height: number, isOnEdit: boolean): Options => {
    const options = (highcharts ?? {}) as Options;
    const chart = options.chart ?? {};

    return {
        ...options,
        chart: {
            ...chart,
            height,
            width,
            zooming: {
                ...chart.zooming,
                type: isOnEdit ? undefined : chart.zooming?.type,
            },
        },
    };
};

const getChartHeight = (hasChart: boolean, hasHtml: boolean, contentHeight: number): number => {
    if (!hasChart) return 0;
    if (!hasHtml) return contentHeight;

    return Math.max(Math.floor((contentHeight - CHART_TEXT_GAP) * 0.6), 0);
};

const getDataFeedbackIcon = (type?: string | null): string => (type === DATA_FEEDBACK_WARNING_TYPE ? 'warning' : 'error');

const BusinessInsight = (props: BusinessInsightProps): ReactElement => {
    const { height, width, isOnEdit, data, decimalSeparator, thousandSeparator, textBlockId, dashboardId, isLoading } = props;
    const hasChart = !!data.highcharts;
    const hasHtml = data.text.length > 0;
    const firstDataFeedback = data.dataFeedback?.[0];
    const contentWidth = Math.max(width - 8, 0);
    const contentHeight = Math.max(height - 8, 0);
    const chartHeight = getChartHeight(hasChart, hasHtml, contentHeight);

    if (!hasChart && !hasHtml && firstDataFeedback) {
        return (
            <ErrorWidget
                width={width}
                height={height}
                errorTitle={firstDataFeedback.title ?? ''}
                errorMessage={firstDataFeedback.body ?? ''}
                icon={getDataFeedbackIcon(firstDataFeedback.type)}
            />
        );
    }

    if (!hasChart) {
        return (
            <TextBlockWidget
                dashboardId={dashboardId}
                textBlockId={textBlockId}
                data={data}
                isLoading={isLoading}
                editable={false}
            />
        );
    }

    return (
        <BusinessInsightWrapper data-testid='business-insight-widget'>
            {chartHeight > 0 && (
                <ChartWrapper $height={chartHeight} $hasHtml={hasHtml}>
                    <Chart
                        data-testid='business-insight-chart'
                        options={getChartOptions(data.highcharts, contentWidth, chartHeight, isOnEdit)}
                        thousandsSep={thousandSeparator}
                        decimalPoint={decimalSeparator}
                    />
                </ChartWrapper>
            )}
            {hasHtml && (
                <HtmlWrapper>
                    <TextBlockWidget
                        dashboardId={dashboardId}
                        textBlockId={textBlockId}
                        data={data}
                        isLoading={isLoading}
                        editable={false}
                    />
                </HtmlWrapper>
            )}
        </BusinessInsightWrapper>
    );
};

export default BusinessInsight;
